import { Crown, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { fmt } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { ShinyButton } from "@/components/ui/ShinyButton";
import { usePrimePurchase } from "@/lib/usePrimePurchase";

export default function AccountPrimePage() {
  const { prime } = useStore();
  const { buy, buying } = usePrimePurchase();
  const m = prime?.membership;
  const state = prime?.state || "none";

  return (
    <div className="mx-auto max-w-2xl px-4 pb-20 pt-28 sm:px-6" data-testid="account-prime-page">
      <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-[#8A6A2F]">Membership</p>
      <h1 className="font-display mt-2 text-4xl font-semibold tracking-tighter text-1">PAQVERO PRIME</h1>

      <div className="mt-8 rounded-3xl border border-line bg-surface p-7" data-testid="prime-status-card">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-sm font-bold text-1"><Crown className="h-4 w-4 text-[#8A6A2F]" /> Status</span>
          <span
            data-testid="prime-status-pill"
            className={`rounded-full px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.16em] ${
              state === "active" ? "bg-emerald-500/15 text-emerald-400"
              : state === "expired" ? "bg-red-500/15 text-red-400"
              : "bg-white/10 text-3"
            }`}
          >
            {state === "active" ? "Active" : state === "expired" ? "Expired" : "Not Subscribed"}
          </span>
        </div>

        {m && state !== "none" && (
          <div className="mt-6 space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-2">Started</span>
              <span className="font-semibold text-1" data-testid="prime-start-date">{m.starts_at ? new Date(m.starts_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-2">Expires</span>
              <span className="font-semibold text-1" data-testid="prime-expiry-date">{m.expires_at ? new Date(m.expires_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-2">Delivery savings left</span>
              <span className="font-semibold text-[#8A6A2F]" data-testid="prime-allowance-left">{fmt(m.remaining)} of {fmt(m.cap_total)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-2">Discounted deliveries used</span>
              <span className="font-semibold text-1" data-testid="prime-uses">{m.uses_count}</span>
            </div>
          </div>
        )}

        {state === "none" && (
          <p className="mt-5 flex items-center gap-2 text-sm text-2">
            <XCircle className="h-4 w-4 text-3" /> You're not a PRIME member yet — save on delivery and unlock member prices.
          </p>
        )}

        {(state === "expired" || state === "none") && (
          <ShinyButton
            data-testid="prime-renew-btn"
            onClick={buy}
            disabled={buying}
            className="mt-7"
            contentClassName="px-7 py-3 text-sm font-bold"
          >
            {buying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crown className="h-4 w-4" />}
            {state === "expired" ? "Renew PRIME" : "Get PRIME"}
          </ShinyButton>
        )}
        {state === "active" && (
          <p className="mt-6 flex items-center gap-2 text-xs text-emerald-400" data-testid="prime-active-note">
            <CheckCircle2 className="h-4 w-4" /> Your member prices and delivery savings apply automatically at checkout.
          </p>
        )}
      </div>
    </div>
  );
}
